import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import PortalLayout from './PortalLayout';
import { useAuth } from '../../context/AuthContext';
import { Inbox, ArrowLeft, Send, Loader2 } from 'lucide-react';
import { trackEvent } from '../../lib/analytics';

const API = process.env.REACT_APP_BACKEND_URL || '';
const MIN_REPLY = 20;

// "Today at 2:30pm" for same-day, "Jun 3" otherwise.
const fmtRowDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) {
      return `Today at ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', timeZone: 'America/Chicago'})}`;
    }
    return d.toLocaleDateString([], { month: 'short', day: 'numeric', timeZone: 'America/Chicago'});
  } catch { return iso; }
};

const fmtMxCity = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      timeZone: 'America/Mexico_City',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }) + ' CDT';
  } catch { return iso; }
};

const authHeaders = () => {
  const t = localStorage.getItem('auth_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// =========================================================================
// Inbox list - top-level view
// =========================================================================
const InboxList = ({ conversations, loading }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="inbox-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#2CA8A0]" />
      </div>
    );
  }
  if (!conversations.length) {
    return (
      <div className="text-center py-20" data-testid="inbox-empty">
        <Inbox className="w-12 h-12 mx-auto mb-4 text-[#5A6B73]" />
        <h2 className="font-display italic text-3xl text-[#0A1F2E] mb-2">No messages yet</h2>
        <p className="text-[#5A6B73] mb-5">
          When you contact a partner through Mundalo your conversations will appear here.
        </p>
        <Link
          to="/portal/partners"
          className="inline-flex items-center gap-2 font-mono uppercase text-[11px] tracking-[0.12em] font-semibold text-[#2CA8A0] hover:underline"
          data-testid="inbox-empty-browse-link"
        >
          Browse partners
        </Link>
      </div>
    );
  }
  return (
    <ul className="divide-y divide-[#EFE6CF] border border-[#EFE6CF] rounded-2xl bg-white overflow-hidden" data-testid="inbox-list">
      {conversations.map((c) => (
        <li key={c.conversation_id}>
          <Link
            to={`/portal/messages/${c.conversation_id}`}
            className="flex items-center gap-4 px-5 py-4 hover:bg-[#F9F6EF] transition"
            data-testid={`inbox-row-${c.conversation_id}`}
          >
            {c.partner?.photo_url ? (
              <img
                src={c.partner.photo_url}
                alt={c.partner.name}
                className="w-12 h-12 rounded-full object-cover border border-[#EFE6CF]"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-[#F4ECD8] flex items-center justify-center text-[#1A5C6E] font-mono text-[10px] uppercase tracking-wider">
                {(c.partner?.category || 'PRO').slice(0, 3)}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2">
                <p className="font-semibold text-[#0A1F2E] truncate" data-testid={`inbox-partner-${c.conversation_id}`}>
                  {c.partner?.name || 'Partner'}
                </p>
                <span className="font-mono uppercase text-[9px] tracking-[0.16em] text-[#5A6B73]">
                  {c.partner?.category || ''}
                </span>
              </div>
              <p className="text-sm text-[#0A1F2E] truncate">{c.subject}</p>
              <p className="text-sm text-[#5A6B73] truncate" data-testid={`inbox-preview-${c.conversation_id}`}>
                {c.last_message_preview || '-'}
              </p>
            </div>
            <div className="flex flex-col items-end gap-1 shrink-0">
              <span className="text-xs text-[#5A6B73] whitespace-nowrap">{fmtRowDate(c.last_message_at)}</span>
              {c.unread_count > 0 && (
                <span
                  className="w-2.5 h-2.5 rounded-full bg-[#E8A830]"
                  data-testid={`inbox-unread-${c.conversation_id}`}
                  title="Unread reply"
                />
              )}
            </div>
          </Link>
        </li>
      ))}
    </ul>
  );
};

// =========================================================================
// Conversation thread view - /portal/messages/:conversationId
// =========================================================================
const ConversationThread = ({ conversationId, onChanged }) => {
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/partner-conversations/${conversationId}`, {
        credentials: 'include', headers: authHeaders(),
      });
      if (!r.ok) {
        setError(r.status === 404 ? 'Conversation not found.' : 'Could not load this conversation.');
        return;
      }
      setThread(await r.json());
    } catch (_) {
      setError('Network error loading this conversation.');
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  useEffect(() => { load(); }, [load]);

  const send = async (e) => {
    e.preventDefault();
    if (reply.length < MIN_REPLY || sending) return;
    setSending(true);
    setSendError('');
    try {
      const r = await fetch(
        `${API}/api/partner-conversations/${conversationId}/member-reply`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({ message_body: reply }),
        }
      );
      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data.success) {
        setSendError(data.detail || 'Could not send your reply. Please try again.');
        return;
      }
      try {
        trackEvent('member_replied', { partner_id: thread?.partner?.id });
      } catch (_) { /* noop */ }
      setReply('');
      await load();
      onChanged?.();
    } catch (_) {
      setSendError('Network error. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-[#2CA8A0]" />
      </div>
    );
  }
  if (error || !thread) {
    return (
      <div className="text-center py-16" data-testid="thread-error">
        <p className="text-[#C0392B] mb-3">{error || 'Conversation unavailable.'}</p>
        <Link to="/portal/messages" className="text-[#2CA8A0] hover:underline">Back to inbox</Link>
      </div>
    );
  }

  const partnerName = thread.partner?.name || 'Partner';

  return (
    <div data-testid="conversation-thread">
      <div className="flex items-center justify-between mb-5">
        <Link
          to="/portal/messages"
          className="inline-flex items-center gap-1.5 text-sm text-[#5A6B73] hover:text-[#0A1F2E] transition"
          data-testid="thread-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back to inbox
        </Link>
      </div>

      <header className="mb-5 pb-5 border-b border-[#EFE6CF]">
        <h1 className="font-display italic text-3xl text-[#0A1F2E]" data-testid="thread-partner-name">
          {partnerName}
        </h1>
        <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#E8A830] mt-1">
          {thread.partner?.category}
        </p>
        <p className="text-sm text-[#0A1F2E] mt-2">{thread.subject}</p>
      </header>

      <div className="space-y-3 mb-6" data-testid="thread-messages">
        {(thread.messages || []).map((m) => {
          const isMember = m.sender_type === 'member';
          return (
            <div
              key={m.message_id}
              className={`flex ${isMember ? 'justify-end' : 'justify-start'}`}
              data-testid={`thread-msg-${m.sender_type}`}
            >
              <div
                className={`max-w-[80%] px-4 py-3 rounded-xl text-sm whitespace-pre-wrap ${
                  isMember
                    ? 'bg-[#E8A830] text-[#0A1F2E]'
                    : 'bg-[#F9F6EF] text-[#0A1F2E] border border-[#EFE6CF]'
                }`}
              >
                <p className={`font-mono uppercase text-[9px] tracking-[0.16em] mb-1 ${
                  isMember ? 'text-[#0A1F2E]/80' : 'text-[#5A6B73]'
                }`}>
                  {isMember ? 'You' : partnerName} · {fmtMxCity(m.sent_at)}
                </p>
                {m.body}
              </div>
            </div>
          );
        })}
      </div>

      <form onSubmit={send} className="border-t border-[#EFE6CF] pt-5" data-testid="thread-reply-form">
        <textarea
          rows={4}
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          placeholder={`Reply to ${partnerName}…`}
          className="w-full px-4 py-3 bg-white border border-[#C4B9A0] text-[#0A1F2E] focus:outline-none focus:border-[#2CA8A0] transition resize-y"
          data-testid="thread-reply-textarea"
        />
        <div className="flex items-center justify-between mt-2">
          <p
            className={`text-xs font-mono uppercase tracking-wider ${
              reply.length >= MIN_REPLY ? 'text-[#1A7F4F]' : 'text-[#5A6B73]'
            }`}
            data-testid="thread-char-counter"
          >
            {reply.length} / {MIN_REPLY} minimum
          </p>
          <button
            type="submit"
            disabled={reply.length < MIN_REPLY || sending}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#E8A830] text-[#0A1F2E] font-bold uppercase tracking-wider text-sm rounded-full hover:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed transition"
            data-testid="thread-reply-submit"
          >
            <Send className="w-4 h-4" /> {sending ? 'Sending…' : 'Send Reply'}
          </button>
        </div>
        {sendError && (
          <p className="text-sm text-[#C0392B] mt-2" data-testid="thread-reply-error">{sendError}</p>
        )}
      </form>
    </div>
  );
};

// =========================================================================
// Page shell - routes both list + thread
// =========================================================================
const PortalMessages = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [conversations, setConversations] = useState([]);
  const [loadingList, setLoadingList] = useState(true);

  // Gate the entire page to authed users.
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/start?return=/portal/messages', { replace: true });
    }
  }, [authLoading, user, navigate]);

  const loadList = useCallback(async () => {
    if (!user?.id) return;
    setLoadingList(true);
    try {
      const r = await fetch(
        `${API}/api/partner-conversations/member/${user.id}`,
        { credentials: 'include', headers: authHeaders() }
      );
      if (r.ok) {
        const d = await r.json();
        setConversations(d.conversations || []);
      }
    } finally {
      setLoadingList(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!conversationId) loadList();
  }, [conversationId, loadList]);

  if (authLoading) {
    return (
      <PortalLayout activeTab="messages">
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-6 h-6 animate-spin text-[#2CA8A0]" />
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout activeTab="messages">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10" data-testid="portal-messages-page">
        {!conversationId && (
          <header className="mb-6">
            <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#2CA8A0] mb-2">
              Partner Network
            </p>
            <h1 className="font-display italic text-4xl text-[#0A1F2E]">Messages</h1>
          </header>
        )}

        {conversationId ? (
          <ConversationThread conversationId={conversationId} onChanged={loadList} />
        ) : (
          <InboxList conversations={conversations} loading={loadingList} />
        )}
      </div>
    </PortalLayout>
  );
};

export default PortalMessages;
